<script lang="ts">
	import { Checkbox, CheckboxSize, HighlightedText, Icon } from '@repo/commons/components'
	import type { TooltipProps } from '$lib_commons/components/Tooltip.svelte'
	import { DEFAULT_TOOLTIP_PROPS } from '@repo/commons/constants'

	interface Props {
		listTitle?: string
		size?: CheckboxSize
		tooltip?: TooltipProps
		items?: string[] | null
		selectedItems?: string[] | null
		highlightPattern?: string
		onHighlightMatch?: (match: string, isExactMatch: boolean) => void
		onchange?: ((items: string[]) => void) | null
	}

	let {
		listTitle = '',
		tooltip,
		items = null,
		size = CheckboxSize.MEDIUM,
		selectedItems = $bindable(null),
		highlightPattern,
		onchange = null,
		onHighlightMatch = () => {}
	}: Props = $props()

	const tooltipProps = $derived({ ...DEFAULT_TOOLTIP_PROPS, ...tooltip })

	function toggleItem(item: string) {
		if (selectedItems?.includes(item)) {
			selectedItems = selectedItems.filter((i) => i !== item)
		} else {
			selectedItems = [...(selectedItems || []), item]
		}
		onchange?.(selectedItems)
	}
</script>

<div class="checkbox-list-container">
	<div class="checkbox-list-title">
		{#if highlightPattern}
			<HighlightedText pattern={highlightPattern} text={listTitle} {onHighlightMatch} />
		{:else}
			{listTitle}
		{/if}
		{#if tooltip?.text || tooltip?.html}
			{#await import('@repo/commons/components') then { Tooltip }}
				<Tooltip {...tooltipProps}>
					<Icon name="info" size="sm" class="text-brand" />
				</Tooltip>
			{/await}
		{/if}
	</div>
	<div class="checkbox-list-items">
		{#if items}
			{#each items as item}
				<div class="checkbox-item">
					<Checkbox
						label={item}
						{size}
						checked={selectedItems?.includes(item)}
						{highlightPattern}
						onchange={() => toggleItem(item)}
						{onHighlightMatch}
					/>
				</div>
			{/each}
		{/if}
	</div>
</div>

<style lang="postcss">
	.checkbox-list-container {
		@apply flex flex-col gap-3;
	}
	.checkbox-list-title {
		@apply text-[#001526]/90 text-base font-[400] leading-normal flex items-center gap-1;
	}
	.checkbox-list-items {
		@apply flex flex-col gap-4;
	}
	.checkbox-item {
		@apply flex items-center gap-3;
	}
	.checkbox-label {
		@apply text-[#001526]/90 text-sm font-normal leading-tight;
	}
</style>
